import React, { useState, useCallback, useRef, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { SyutSelect, type SelectOption, SyutGrid, type SyutGridColumn } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { reportService, coaLedgerService } from 'api/services';
import type { OpenBillsResultDto } from 'api/types';
import { useHasPermission } from 'hooks/usePermissions';
import ReportHeader from 'components/reports/ReportHeader';
import DrillableCell from 'components/reports/DrillableCell';

const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');

interface OpenBillReportScreenProps {
  moduleIcon?: string;
  tabId?: string;
}

// Unified row type — SyutGrid's getSubRows requires the same shape for parent and children.
// isBill=true → the fields from OpenBillDto are populated; isBill=false → allocation fields.
interface OpenBillGridRow {
  id: string;
  isBill: boolean;
  // Bill fields
  referenceNo?: string;
  itemDate?: string;
  dueDate?: string | null;
  drCr?: string;
  itemKind?: string;
  currencyCode?: string;
  fcOriginalAmount?: number;
  fcBalance?: number;
  itemStatus?: string;
  // ADR-0060: "Short-paid on INV-12 · Rate difference · note" for a deduction item
  deductionNote?: string;
  // Allocation fields
  allocationDate?: string;
  settlingNo?: string;
  fcAmount?: number;
  realizedFxAmount?: number;
  // Sub-rows only on bills
  subRows?: OpenBillGridRow[];
}

const OpenBillReportScreen: React.FC<OpenBillReportScreenProps> = ({ moduleIcon, tabId }) => {
  const canExport = useHasPermission('open-bill-report', 'EXPORT');
  const [filterOpen, setFilterOpen] = useState(false);
  const [ledgerId, setLedgerId] = useState('');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [deductionsOnly, setDeductionsOnly] = useState(false);
  const [result, setResult] = useState<OpenBillsResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [ran, setRan] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const labelCache = useRef<Map<string, string>>(new Map());
  const ledgerLabel = ledgerId ? (labelCache.current.get(ledgerId) || '') : '';

  // Flatten bills + allocations into SyutGrid's row-expansion shape.
  const gridRows: OpenBillGridRow[] = useMemo(() => {
    if (!result) return [];
    return result.items.map(b => ({
      id: b.id,
      isBill: true,
      referenceNo: b.referenceNo,
      itemDate: b.itemDate,
      dueDate: b.dueDate,
      drCr: b.drCr,
      itemKind: b.itemKind,
      currencyCode: b.currencyCode,
      fcOriginalAmount: b.fcOriginalAmount,
      fcBalance: b.fcBalance,
      itemStatus: b.itemStatus,
      deductionNote: b.deductionKind
        ? [
            `${b.deductionKind === 'WITHHELD' ? 'Withheld' : 'Short-paid'}${b.sourceReferenceNo ? ` on ${b.sourceReferenceNo}` : ''}`,
            b.reasonLabel, b.remarks,
          ].filter(Boolean).join(' · ')
        : undefined,
      subRows: b.allocations.map((a, i) => ({
        id: `${b.id}-alloc-${i}`,
        isBill: false,
        allocationDate: a.allocationDate,
        settlingNo: a.settlingNo,
        fcAmount: a.fcAmount,
        realizedFxAmount: a.realizedFxAmount,
      })),
    }));
  }, [result]);

  const totalOutstanding = result?.totalOutstanding ?? 0;

  const loadLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      // Open Bills only makes sense for open-item-managed ledgers (ADR-0013). Filter at
      // the source so the picker never surfaces a ledger the report will reject.
      const res = await coaLedgerService.getAll({
        pageSize: 50,
        searchTerm: input || undefined,
        isOpenItemManaged: true,
      });
      res.data.forEach(l => labelCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch {
      return [];
    }
  }, []);

  const load = useCallback(async (targetPage: number) => {
    if (!ledgerId) { notificationService.toast.warning('Select a ledger.'); return; }
    setLoading(true);
    try {
      setResult(await reportService.getOpenBills(ledgerId, includeClosed, targetPage, pageSize, deductionsOnly));
      setPage(targetPage);
      setRan(true);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load open bills.');
    } finally {
      setLoading(false);
    }
  }, [ledgerId, includeClosed, deductionsOnly]);

  const run = useCallback(() => load(1), [load]);

  const clear = useCallback(
    () => { setLedgerId(''); setIncludeClosed(false); setDeductionsOnly(false); setResult(null); setRan(false); setPage(1); },
    [],
  );

  const handleExport = useCallback(async () => {
    if (!ledgerId) { notificationService.toast.warning('Select a ledger to export.'); return; }
    setExporting(true);
    try {
      await reportService.exportOpenBills(ledgerId, includeClosed, deductionsOnly);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not export open bills.');
    } finally {
      setExporting(false);
    }
  }, [ledgerId, includeClosed, deductionsOnly]);

  // Export button appears only when the user has UI.OPEN-BILL-REPORT.EXPORT permission.
  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(run),
    ...(canExport ? [{ ...actionButtonFactory.export_(handleExport), disabled: exporting }] : []),
  ];

  // ------------------------------------------------------------------
  // SyutGrid columns. Cell renderers branch on row.original.isBill so that
  // parent (bill) and child (allocation) rows share the column axis but
  // show different content per column.
  //
  // Drill-down TODO: wrap referenceNo (bill) and settlingNo (allocation)
  // in DrillableCell with onClick handlers to open the source voucher.
  // ------------------------------------------------------------------
  const columns: SyutGridColumn<OpenBillGridRow>[] = useMemo(() => [
    {
      id: 'reference',
      header: 'Reference',
      cell: ({ row }) => {
        const r = row.original;
        if (r.isBill) {
          return (
            <DrillableCell>
              <strong>{r.referenceNo}</strong>
              {r.deductionNote && (
                <div title={r.deductionNote} style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: 400 }}>
                  {r.deductionNote}
                </div>
              )}
            </DrillableCell>
          );
        }
        // Allocation row: indented "↳ settled by …" label
        return (
          <span style={{ paddingLeft: '1.5rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            ↳ Settled by <DrillableCell>{r.settlingNo || '—'}</DrillableCell>
          </span>
        );
      },
    },
    {
      id: 'itemDate',
      header: 'Date',
      size: 110,
      cell: ({ row }) => {
        const r = row.original;
        if (r.isBill) return fmtDate(r.itemDate);
        return <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>{fmtDate(r.allocationDate)}</span>;
      },
    },
    {
      id: 'dueDate',
      header: 'Due',
      size: 110,
      cell: ({ row }) => row.original.isBill ? fmtDate(row.original.dueDate) : '',
    },
    {
      id: 'kind',
      header: 'Kind',
      size: 90,
      cell: ({ row }) => row.original.isBill ? (row.original.itemKind || '—') : '',
    },
    {
      id: 'side',
      header: 'Side',
      size: 70,
      cell: ({ row }) => row.original.isBill ? (row.original.drCr || '—') : '',
    },
    {
      id: 'original',
      header: 'Original',
      align: 'right',
      size: 130,
      cell: ({ row }) => {
        const r = row.original;
        return r.isBill
          ? `${money(r.fcOriginalAmount ?? 0)} ${r.currencyCode ?? ''}`
          : '';
      },
    },
    {
      id: 'balance',
      header: 'Balance',
      align: 'right',
      size: 120,
      cell: ({ row }) => {
        const r = row.original;
        if (r.isBill) return money(r.fcBalance ?? 0);
        // Allocation row shows the settled amount (fcAmount) in this column
        return (
          <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            {money(r.fcAmount ?? 0)}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      size: 110,
      cell: ({ row }) => {
        const r = row.original;
        if (r.isBill) {
          return (
            <span className="badge" style={r.itemStatus === 'CLOSED' ? undefined : { background: '#6b7280', color: '#fff' }}>
              {r.itemStatus}
            </span>
          );
        }
        // Allocation row: show realized FX (if any)
        return r.realizedFxAmount
          ? <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>FX {money(r.realizedFxAmount)}</span>
          : '';
      },
    },
  ], []);

  return (
    <EnterpriseScreenShell
      title="Open Bills"
      description="Outstanding bills and their complete allocation history for a ledger"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Ledger" isAsync loadOptions={loadLedgers}
                  defaultOptions cacheOptions={false}
                  seedOptions={ledgerId
                    ? [{ value: ledgerId, label: labelCache.current.get(ledgerId) || '' }]
                    : undefined}
                  menuPortalTarget={document.body} value={ledgerId}
                  onChange={v => setLedgerId((v as string) || '')}
                  placeholder="Pick an open-item ledger" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeClosed}
                    onChange={e => setIncludeClosed(e.target.checked)} /> Include closed
                </label>
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={deductionsOnly}
                    onChange={e => setDeductionsOnly(e.target.checked)} /> Short-paid / withheld only
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clear}>Clear</button>
              <button className="btn btn-primary" onClick={run} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader
        filters={[
          { label: 'Ledger', value: ledgerLabel },
          { label: 'Include closed', value: includeClosed ? 'Yes' : 'No' },
          ...(deductionsOnly ? [{ label: 'Items', value: 'Short-paid / withheld only' }] : []),
        ]}
      />
      {!ran && (
        <div style={{
          color: 'var(--text-muted)', padding: '2rem', textAlign: 'center',
          border: '1px solid var(--border-color)', borderTop: 'none',
          borderRadius: '0 0 0.5rem 0.5rem',
        }}>
          Pick a ledger and click Apply.
        </div>
      )}
      {ran && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
          border: '1px solid var(--border-color)', borderTop: 'none',
          borderRadius: '0 0 0.5rem 0.5rem', overflow: 'hidden',
        }}>
          {/* Flex column so SyutGrid (flex: 1) gets a bounded height and scrolls; a block parent
              with overflow: hidden clipped the later rows and the pager. */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <SyutGrid<OpenBillGridRow>
              data={gridRows}
              columns={columns}
              isLoading={loading}
              emptyMessage="No bills found"
              enableExpanding
              expanded={true}                                 // Always expand: users need the full allocation history visible.
              getSubRows={(row) => row.subRows}
              enablePagination
              manualPagination
              totalCount={result?.totalCount ?? 0}
              currentPage={page - 1}
              pageSize={pageSize}
              onPageChange={(idx) => load(idx + 1)}
            />
          </div>
          {/* Total outstanding band under the grid — server-side rollup across all matching bills. */}
          {result && result.items.length > 0 && (
            <div style={{
              padding: '0.6rem 0.75rem',
              background: 'var(--bg-secondary)',
              borderTop: '1px solid var(--border-color)',
              fontSize: '0.8125rem',
              fontWeight: 700,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ color: 'var(--text-secondary)' }}>Total Outstanding (all matching bills)</span>
              <span>{money(totalOutstanding)}</span>
            </div>
          )}
        </div>
      )}
    </EnterpriseScreenShell>
  );
};

export default OpenBillReportScreen;
